"use client";

import { useState, useCallback } from "react";
import ShareSheet from "./ShareSheet";

const ICON_SIZE = { sm: 36, md: 44, lg: 52 };
const FAB_SIZE = { sm: 38, md: 44, lg: 52 };

const VARIANT_STYLE = {
  icon: {
    backgroundColor: "transparent",
    color: "var(--color-text)",
  },
  iconSolid: {
    backgroundColor: "var(--color-cardBg)",
    color: "var(--color-text)",
    border: "1px solid var(--color-cardBorder)",
  },
  iconTransparent: {
    backgroundColor: "rgba(255,255,255,0.85)",
    color: "#000",
    backdropFilter: "blur(4px)",
  },
};

function ShareSvg({ size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

export default function ShareButton({
  payload,
  size = "md",
  variant = "icon",
  placement = "card",
  className = "",
  emoji,
  label,
  pulse = false,
  onShareStart,
  onShareEnd,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleClick = useCallback(
    async (e) => {
      e.stopPropagation();
      e.preventDefault();
      onShareStart?.();
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function"
      ) {
        try {
          await navigator.share(payload);
          onShareEnd?.();
          return;
        } catch (err) {
          if (err && err.name === "AbortError") {
            onShareEnd?.();
            return;
          }
        }
      }
      setSheetOpen(true);
      onShareEnd?.();
    },
    [payload, onShareStart, onShareEnd],
  );

  const ariaLabel = label || "分享";

  // Pill variant: emoji + label + accent bg, secondary-CTA style
  if (variant === "pill") {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          aria-label={ariaLabel}
          className={`inline-flex items-center justify-center gap-1.5 text-sm font-medium rounded-full transition-all duration-200 hover:-translate-y-[1px] hover:scale-[1.04] active:scale-[0.96] ${className}`}
          style={{
            padding: size === "sm" ? "8px 14px" : "10px 18px",
            minHeight: size === "sm" ? "36px" : "44px",
            backgroundColor: "var(--color-accent)",
            // a11y fix (axe: color-contrast 2.05:1 → WCAG AA):
            // use accentText (dark) on the pink accent bg instead of #fff
            color: "var(--color-accentText, #1A1A1A)",
            boxShadow: "0 2px 6px rgba(231, 164, 180, 0.35)",
            WebkitTapHighlightColor: "transparent",
            cursor: "pointer",
            border: "none",
            whiteSpace: "nowrap",
          }}
          data-testid="share-button"
        >
          {emoji && (
            <span
              aria-hidden="true"
              style={{ fontSize: "16px", lineHeight: 1 }}
            >
              {emoji}
            </span>
          )}
          <span>{label || "分享"}</span>
        </button>
        <ShareSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          payload={payload}
          placement={placement}
        />
      </>
    );
  }

  // FAB variant: circular filled button with emoji, optional pulse
  if (variant === "fab") {
    const fab = FAB_SIZE[size];
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          aria-label={ariaLabel}
          className={`flex items-center justify-center rounded-full transition-all duration-200 hover:scale-110 hover:-translate-y-[1px] active:scale-95 ${pulse ? "animate-share-pulse" : ""} ${className}`}
          style={{
            width: `${fab}px`,
            height: `${fab}px`,
            backgroundColor: "var(--color-accent)",
            color: "#fff",
            boxShadow: "0 3px 10px rgba(231, 164, 180, 0.45)",
            WebkitTapHighlightColor: "transparent",
            cursor: "pointer",
            border: "2px solid #fff",
          }}
          data-testid="share-button"
        >
          <span
            aria-hidden="true"
            style={{ fontSize: size === "sm" ? "18px" : "20px", lineHeight: 1 }}
          >
            {emoji || "🐾"}
          </span>
        </button>
        <ShareSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          payload={payload}
          placement={placement}
        />
      </>
    );
  }

  // Default icon variant (legacy, kept for compatibility)
  const iconTap = ICON_SIZE[size] || ICON_SIZE.md;
  const iconSize = size === "sm" ? 16 : 20;
  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={ariaLabel}
        className={`flex items-center justify-center rounded-full transition-opacity hover:opacity-80 ${className}`}
        style={{
          width: `${iconTap}px`,
          height: `${iconTap}px`,
          ...VARIANT_STYLE[variant],
          WebkitTapHighlightColor: "transparent",
          cursor: "pointer",
          border: VARIANT_STYLE[variant].border ?? "none",
        }}
        data-testid="share-button"
      >
        <ShareSvg size={iconSize} />
      </button>
      <ShareSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        payload={payload}
        placement={placement}
      />
    </>
  );
}
